"use client";
import React from "react";
import Image from "components/common/Image";
import { formatPrice } from "utils/currencyFormatter";
import { useT } from "providers/I18nProvider";
import dayjs from "utils/day";
import type { PackageInfo } from "services/prepaidPackage/formatter";

/** 890000 -> "890k" cho ô số liệu nhỏ */
const formatShort = (n: number) =>
  n >= 1000 ? `${Math.round(n / 1000)}k` : String(n || 0);

/**
 * Thẻ gói trả trước — dùng chung cho dải cuộn ngang trang chủ và lưới trang /goi-tiet-kiem.
 * Chiều rộng do nơi gọi quyết định qua className.
 *
 * `compact` (dải trang chủ, mockup A 05/10): TRÊN MOBILE thu gọn — ảnh ngang thấp, bỏ dải
 * "bỏ túi…", 3 ô số → 1 dòng "10 buổi · 12 tháng · 20k/buổi", 1 nút "Xem gói"
 * (nút "Mua trên app" đã có trong modal chi tiết). Từ md trở lên vẫn là thẻ đầy đủ.
 */
const PackageCard: React.FC<{
  pkg: PackageInfo;
  onSelect: (pkg: PackageInfo) => void;
  onBuy: () => void;
  className?: string;
  compact?: boolean;
}> = ({ pkg, onSelect, onBuy, className = "", compact = false }) => {
  const t = useT();
  const shopLabel =
    pkg.shops.length > 1
      ? `${pkg.chainName || pkg.shops[0].name} · ${t("home.packageBranches", {
          count: pkg.shops.length,
        })}`
      : pkg.shops[0]?.name || pkg.chainName;
  const daysLeft = pkg.endDate
    ? Math.max(0, dayjs(pkg.endDate).endOf("day").diff(dayjs(), "day"))
    : 0;
  const kicker = [
    t("home.packagePlan"),
    shopLabel,
    daysLeft > 0 && daysLeft <= 30 ? t("home.packageDaysLeft", { count: daysLeft }) : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const durationLabel = pkg.durationMonths
    ? `${pkg.durationMonths} ${t("home.unitMonths")}`
    : `${pkg.durationDays} ${t("home.unitDays")}`;

  // Các khối chỉ hiện trên desktop khi compact
  const fullOnly = compact ? "hidden md:block" : "";
  const fullOnlyGrid = compact ? "hidden md:grid" : "grid";
  const fullOnlyFlex = compact ? "hidden md:flex" : "flex";

  return (
    <div
      onClick={() => onSelect(pkg)}
      className={`group rounded-2xl bg-white overflow-hidden shadow-sm border border-[#d8f3f2] flex flex-col cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${className}`}
    >
      <div className="relative bg-gray-100 overflow-hidden">
        <Image
          src={pkg.cover}
          alt={pkg.coverAlt}
          wrapClassName={compact ? "w-full h-[110px] md:h-auto md:aspect-[4/3]" : "w-full aspect-[4/3]"}
          className="transition-transform duration-300 group-hover:scale-[1.04]"
          sizes="(min-width: 768px) 262px, 150px"
        />
        {pkg.savingPct > 0 && (
          <span className="absolute left-2 top-2 md:left-2.5 md:top-2.5 rounded-full bg-pink-400 px-2 py-0.5 text-[12px] font-bold text-white shadow">
            {t("home.packageSave")} {pkg.savingPct}%
          </span>
        )}
      </div>
      {pkg.savingAmount > 0 && (
        <div
          className={`bg-[#fdf2f8] border-y border-pink-100 text-center py-1.5 text-[12px] font-extrabold text-pink-500 ${fullOnly}`}
        >
          {t("home.packageRibbon", {
            count: pkg.quantity,
            amount: formatPrice(pkg.savingAmount),
          })}{" "}
          🎉
        </div>
      )}
      <div
        className={`flex flex-col flex-1 ${
          compact ? "p-2.5 gap-2 md:p-3.5 md:gap-3" : "p-3.5 gap-3"
        }`}
      >
        {/* Tên + số liệu */}
        <div className="flex flex-col gap-2 min-w-0">
          <div>
            <p className="text-[12px] font-bold text-primary uppercase tracking-wide truncate !mb-0">
              {compact ? (
                <>
                  <span className="md:hidden">{shopLabel}</span>
                  <span className="hidden md:inline">{kicker}</span>
                </>
              ) : (
                kicker
              )}
            </p>
            <h3
              className={`font-bold text-black01 leading-[1.3] mt-0.5 line-clamp-2 min-h-[2.6rem] !mb-0 ${
                compact ? "text-[13px] md:text-[15px]" : "text-[15px]"
              }`}
            >
              {pkg.name}
            </h3>
          </div>
          {/* Mobile compact: 1 dòng meta thay cho 3 ô số */}
          {compact && (
            <p className="md:hidden text-[12px] text-gray-500 !mb-0 leading-snug">
              <b className="text-pink-500 font-extrabold">
                {formatShort(pkg.pricePerUse)}/{t("home.unitSessions")}
              </b>
              <br />
              {pkg.quantity} {t("home.unitSessions")} · {durationLabel}
            </p>
          )}
          <div className={`${fullOnlyGrid} grid-cols-3 gap-1.5`}>
            <div className="rounded-lg bg-[#eefbfb] py-1.5 px-1 text-center">
              <p className="text-sm font-extrabold text-black01 !mb-0">{pkg.quantity}</p>
              <p className="text-[12px] text-gray-500 !mb-0">{t("home.unitSessions")}</p>
            </div>
            <div className="rounded-lg bg-[#eefbfb] py-1.5 px-1 text-center">
              <p className="text-sm font-extrabold text-black01 !mb-0">
                {pkg.durationMonths || pkg.durationDays}
              </p>
              <p className="text-[12px] text-gray-500 !mb-0">
                {pkg.durationMonths ? t("home.unitMonths") : t("home.unitDays")}
              </p>
            </div>
            <div className="rounded-lg bg-[#fdf2f8] py-1.5 px-1 text-center">
              <p className="text-sm font-extrabold text-pink-500 !mb-0">
                {formatShort(pkg.pricePerUse)}
              </p>
              <p className="text-[12px] text-gray-500 !mb-0">/{t("home.unitSessions")}</p>
            </div>
          </div>
        </div>
        {/* Giá + tiết kiệm + CTA */}
        <div
          className={`flex flex-col flex-1 border-t border-[#e6f7f7] ${
            compact ? "gap-1.5 pt-2 md:gap-2 md:pt-2.5" : "gap-2 pt-2.5"
          }`}
        >
          <div className="flex items-end gap-1.5 md:gap-2 flex-wrap">
            <p
              className={`font-extrabold text-black01 !mb-0 ${
                compact ? "text-[15px] md:text-xl" : "text-xl"
              }`}
            >
              {formatPrice(pkg.price)}
            </p>
            {pkg.originalTotal > pkg.price ? (
              <p className="text-xs text-gray-400 line-through pb-0.5 !mb-0">
                {formatPrice(pkg.originalTotal)}
              </p>
            ) : null}
            {pkg.savingPct > 0 && (
              <span
                className={`ml-auto rounded-full bg-pink-400 text-white text-[12px] font-extrabold px-2 py-0.5 ${fullOnly}`}
              >
                –{pkg.savingPct}%
              </span>
            )}
          </div>
          <div className="flex gap-2 mt-auto pt-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSelect(pkg);
              }}
              className="flex-1 rounded-full bg-primary text-white font-bold text-xs py-1.5 md:py-2 hover:opacity-90 transition-opacity"
            >
              {t("home.viewPackage")}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onBuy();
              }}
              className={`${fullOnlyFlex} flex-1 items-center justify-center rounded-full border border-primary text-primary font-bold text-xs py-2 px-2 hover:bg-[#eefbfb] transition-colors`}
            >
              {t("home.buyOnApp")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageCard;
